import { useState, useEffect, useCallback, memo, useMemo } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { 
  Calendar, Clock, Euro, Users, ChevronRight, ChevronLeft, 
  MapPin, User, Mail, Phone, MessageSquare, CreditCard, 
  Building, CheckCircle2, Loader2, Sparkles, Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ProviderType, UniversalBookingService, getBookingConfig } from "@/types/booking";
import { getBookingTheme, STEP_ICONS } from "@/config/bookingThemes";
import { useUniversalServices } from "@/hooks/useUniversalServices";
import { useUniversalAvailability } from "@/hooks/useUniversalAvailability";
import { useUniversalLocations } from "@/hooks/useUniversalLocations";
import { useUniversalBookings } from "@/hooks/useUniversalBookings";
import { useIsMobile } from "@/hooks/use-mobile";
import { AvailabilityCalendar } from "./AvailabilityCalendar";
import { LocationSelector } from "./LocationSelector";
import { PaymentMethodSelector } from "./PaymentMethodSelector";

interface UniversalBookingWidgetProps {
  providerType: ProviderType;
  providerId: string;
  providerName: string;
  providerSlug?: string;
  className?: string;
}

type Step = "service" | "location" | "datetime" | "details" | "payment" | "confirmation";

interface BookingData {
  service: UniversalBookingService | null;
  selectedLocationId: string | null;
  date: Date | null;
  startTime: string;
  endTime: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  notes: string;
  paymentMethod: "stripe" | "paypal" | "in_person";
}

const initialBookingData: BookingData = {
  service: null,
  selectedLocationId: null,
  date: null,
  startTime: "",
  endTime: "",
  clientName: "",
  clientEmail: "",
  clientPhone: "",
  notes: "",
  paymentMethod: "stripe",
};

// Premium service card with theme support
const ServiceCard = memo(({ 
  service, 
  isSelected, 
  onSelect,
  theme,
}: { 
  service: UniversalBookingService; 
  isSelected: boolean; 
  onSelect: () => void;
  theme: ReturnType<typeof getBookingTheme>;
}) => {
  const ThemeIcon = theme.icon;
  
  return (
    <Card
      className={cn(
        "group cursor-pointer overflow-hidden transition-all duration-300 touch-manipulation",
        "hover:shadow-xl hover:-translate-y-1 border-2",
        "hover:border-primary/50",
        isSelected && "border-primary ring-2 ring-primary/20"
      )}
      onClick={onSelect}
    >
      {/* Top gradient line */}
      <div className={cn(
        "absolute top-0 left-0 w-full h-1 transition-opacity duration-300",
        `bg-gradient-to-r ${theme.gradient}`,
        isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
      )} />
      
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start gap-4">
          {/* Icon container with theme gradient */}
          <div className={cn(
            "w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0",
            "transition-all duration-300 group-hover:scale-110",
            isSelected 
              ? `bg-gradient-to-br ${theme.gradient}`
              : "bg-gradient-to-br from-primary/10 to-primary/5"
          )}>
            <ThemeIcon className={cn(
              "h-6 w-6 sm:h-7 sm:w-7",
              isSelected ? "text-white" : "text-primary"
            )} />
          </div>
          
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-base sm:text-lg leading-tight">{service.name}</h4>
            {service.description && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                {service.description}
              </p>
            )}
            {/* Info pills */}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Badge variant="secondary" className="rounded-full text-xs">
                <Clock className="h-3 w-3 mr-1" />
                {service.duration_minutes} min
              </Badge>
              {service.max_participants > 1 && (
                <Badge variant="secondary" className="rounded-full text-xs">
                  <Users className="h-3 w-3 mr-1" />
                  Max {service.max_participants}
                </Badge>
              )}
              <Badge variant="outline" className="rounded-full text-xs">
                {service.location_type === "online"
                  ? "🌐 Online"
                  : service.location_type === "hybrid"
                  ? "🔄 Híbrido"
                  : "📍 Presencial"}
              </Badge>
            </div>
          </div>
          
          {/* Price */}
          <div className="text-right flex-shrink-0">
            <span className={cn(
              "text-2xl sm:text-3xl font-bold",
              `bg-gradient-to-r ${theme.gradient} bg-clip-text text-transparent`
            )}>
              {service.price}€
            </span>
            <ChevronRight className="h-5 w-5 text-muted-foreground mt-1 ml-auto group-hover:text-primary group-hover:translate-x-1 transition-all" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
});
ServiceCard.displayName = "ServiceCard";

// Premium booking summary card
const BookingSummary = memo(({ 
  service, 
  date, 
  startTime, 
  endTime,
  clientName,
  clientEmail,
  theme,
  compact = false
}: { 
  service: UniversalBookingService | null;
  date: Date | null;
  startTime?: string;
  endTime?: string;
  clientName?: string;
  clientEmail?: string;
  theme: ReturnType<typeof getBookingTheme>;
  compact?: boolean;
}) => {
  if (!service) return null;
  const ThemeIcon = theme.icon;
  
  return (
    <Card className={cn(
      "overflow-hidden border-2",
      compact ? "border-muted" : "border-primary/20"
    )}>
      <div className={cn(
        "h-1 w-full",
        `bg-gradient-to-r ${theme.gradient}`
      )} />
      <CardContent className={cn("space-y-2", compact ? "p-3" : "p-4")}>
        <div className="flex items-center gap-2">
          <ThemeIcon className="h-4 w-4 text-primary" />
          <p className={cn("font-semibold", compact ? "text-sm" : "text-base")}>{service.name}</p>
        </div>
        {date && (
          <p className="text-xs text-muted-foreground">
            📅 {format(date, "EEEE d 'de' MMMM", { locale: es })}
            {startTime && ` • ⏰ ${startTime}`}
            {endTime && ` - ${endTime}`}
          </p>
        )}
        {clientName && (
          <p className="text-xs text-muted-foreground">
            👤 {clientName} {clientEmail && `• ${clientEmail}`}
          </p>
        )}
        <p className={cn(
          "font-bold",
          compact ? "text-sm" : "text-lg",
          `bg-gradient-to-r ${theme.gradient} bg-clip-text text-transparent`
        )}>
          {service.price}€
        </p>
      </CardContent>
    </Card>
  );
});
BookingSummary.displayName = "BookingSummary";

// Step label mapping for accessibility
const STEP_LABELS: Record<Step, string> = {
  service: "Seleccionar servicio",
  location: "Elegir ubicación",
  datetime: "Fecha y hora",
  details: "Datos de contacto",
  payment: "Método de pago",
  confirmation: "Confirmación",
};

// Premium step indicator with tooltips
const StepIndicator = memo(({ 
  steps, 
  currentStep, 
  theme 
}: { 
  steps: Step[];
  currentStep: Step;
  theme: ReturnType<typeof getBookingTheme>;
}) => {
  const stepIndex = steps.indexOf(currentStep);
  const totalSteps = steps.length;
  
  return (
    <div className="space-y-2">
      {/* Step counter label */}
      <p className="text-center text-xs text-white/80 font-medium">
        Paso {stepIndex + 1} de {totalSteps}
      </p>
      
      <div className="flex items-center justify-between px-2 relative">
        {steps.map((step, i) => (
          <div 
            key={step} 
            className="flex flex-col items-center gap-1 z-10"
            title={STEP_LABELS[step]}
          >
            <div className={cn(
              "w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-300",
              i < stepIndex && "bg-green-500 text-white",
              i === stepIndex && `bg-gradient-to-br ${theme.gradient} text-white shadow-lg scale-110`,
              i > stepIndex && "bg-white/20 text-white/60"
            )}>
              {i < stepIndex ? (
                <Check className="h-4 w-4 sm:h-5 sm:w-5" aria-label="Completado" />
              ) : (
                <span className="text-sm" aria-label={STEP_LABELS[step]}>{STEP_ICONS[step]}</span>
              )}
            </div>
          </div>
        ))}
        {/* Progress line */}
        <div className="absolute left-0 right-0 top-[18px] sm:top-[20px] h-0.5 bg-white/20 mx-8">
          <div 
            className={cn("h-full transition-all duration-500 bg-green-500")}
            style={{ width: `${(stepIndex / (totalSteps - 1)) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
});
StepIndicator.displayName = "StepIndicator";

function UniversalBookingWidgetContent({
  providerType,
  providerId,
  providerName,
  providerSlug,
  onClose,
}: UniversalBookingWidgetProps & { onClose: () => void }) {
  const [currentStep, setCurrentStep] = useState<Step>("service");
  const [bookingData, setBookingData] = useState<BookingData>(initialBookingData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookedSlots, setBookedSlots] = useState<{ date: string; start_time: string; end_time: string }[]>([]);
  const isMobile = useIsMobile();

  const config = useMemo(() => getBookingConfig(providerType), [providerType]);
  const theme = useMemo(() => getBookingTheme(providerType), [providerType]);
  const { activeServices, isLoading: servicesLoading } = useUniversalServices(providerType, providerId);
  const { activeAvailability, blockedDates } = useUniversalAvailability(providerType, providerId);
  const { activeLocations } = useUniversalLocations(providerType, providerId);
  const { createBooking, getBookedSlotsForDate } = useUniversalBookings(providerType, providerId);

  const ThemeIcon = theme.icon;

  // Dynamic steps based on locations
  const hasMultipleLocations = activeLocations.length > 1;
  const steps: Step[] = useMemo(() => {
    if (hasMultipleLocations) {
      return ["service", "location", "datetime", "details", "payment", "confirmation"];
    }
    return ["service", "datetime", "details", "payment", "confirmation"];
  }, [hasMultipleLocations]);
  
  const stepIndex = steps.indexOf(currentStep);

  // Auto-select single location
  useEffect(() => {
    if (activeLocations.length === 1 && !bookingData.selectedLocationId) {
      setBookingData(prev => ({ ...prev, selectedLocationId: activeLocations[0].id }));
    }
  }, [activeLocations, bookingData.selectedLocationId]);

  // Load user data if logged in
  useEffect(() => {
    const loadUserData = async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (userData.user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", userData.user.id)
          .single();

        setBookingData((prev) => ({
          ...prev,
          clientName: profile?.full_name || "",
          clientEmail: userData.user?.email || "",
        }));
      }
    };
    loadUserData();
  }, []);

  // Load booked slots when date changes
  useEffect(() => {
    if (bookingData.date) {
      const dateStr = format(bookingData.date, "yyyy-MM-dd");
      getBookedSlotsForDate(dateStr).then((slots) => {
        setBookedSlots(slots.map(s => ({ ...s, date: dateStr })));
      });
    }
  }, [bookingData.date, getBookedSlotsForDate]);

  const handleSelectService = useCallback((service: UniversalBookingService) => {
    setBookingData((prev) => ({ ...prev, service }));
    setCurrentStep(hasMultipleLocations ? "location" : "datetime");
  }, [hasMultipleLocations]);

  const handleSelectLocation = useCallback((locationId: string) => {
    setBookingData((prev) => ({ ...prev, selectedLocationId: locationId }));
    setCurrentStep("datetime");
  }, []);

  const handleSelectSlot = useCallback((date: Date, startTime: string, endTime: string) => {
    setBookingData((prev) => ({
      ...prev,
      date,
      startTime,
      endTime,
    }));
    setCurrentStep("details");
  }, []);

  const handleBack = useCallback(() => {
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  }, [currentStep, steps]);

  const handleCreateBooking = async () => {
    if (!bookingData.service || !bookingData.date) {
      toast.error("Datos de reserva incompletos");
      return;
    }

    setIsSubmitting(true);
    try {
      const booking = await createBooking({
        provider_type: providerType,
        provider_id: providerId,
        service_id: bookingData.service.id,
        selected_location_id: bookingData.selectedLocationId || undefined,
        booking_date: format(bookingData.date, "yyyy-MM-dd"),
        start_time: bookingData.startTime,
        end_time: bookingData.endTime,
        client_name: bookingData.clientName,
        client_email: bookingData.clientEmail,
        client_phone: bookingData.clientPhone,
        client_notes: bookingData.notes,
        payment_method: bookingData.paymentMethod,
        total_amount: bookingData.service.price,
      });

      if (!booking) throw new Error("Error creating booking");

      if (bookingData.paymentMethod === "stripe") {
        const { data, error } = await supabase.functions.invoke(
          "create-universal-booking-checkout",
          {
            body: {
              booking_id: booking.id,
              provider_type: providerType,
              provider_id: providerId,
              service_id: bookingData.service.id,
              service_name: bookingData.service.name,
              provider_name: providerName,
              amount: bookingData.service.price,
              client_email: bookingData.clientEmail,
              booking_date: format(bookingData.date, "yyyy-MM-dd"),
              start_time: bookingData.startTime,
            },
          }
        );

        if (error || !data?.url) throw new Error(error?.message || "Error creating Stripe checkout");
        window.location.href = data.url;
        return;
      }

      if (bookingData.paymentMethod === "paypal") {
        const { data, error } = await supabase.functions.invoke(
          "create-paypal-booking-order",
          {
            body: {
              booking_id: booking.id,
              trainer_id: providerId,
              service_name: bookingData.service.name,
              trainer_name: providerName,
              amount: bookingData.service.price,
              booking_date: format(bookingData.date, "yyyy-MM-dd"),
              start_time: bookingData.startTime,
            },
          }
        );

        if (error || !data?.approveUrl) throw new Error(error?.message || "Error creating PayPal order");
        window.location.href = data.approveUrl;
        return;
      }

      // In-person payment - just confirm
      setCurrentStep("confirmation");
      toast.success("¡Reserva creada exitosamente!");
    } catch (err) {
      console.error("Error creating booking:", err);
      toast.error("Error al crear la reserva");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetBooking = useCallback(() => {
    setBookingData(initialBookingData);
    setCurrentStep("service");
    onClose();
  }, [onClose]);

  const getStepTitle = () => {
    switch (currentStep) {
      case "service": return `Elige tu ${config.serviceLabels.singular.toLowerCase()}`;
      case "location": return "¿Dónde quieres entrenar?";
      case "datetime": return "Elige fecha y hora";
      case "details": return "Tus datos de contacto";
      case "payment": return "Método de pago";
      case "confirmation": return "¡Reserva confirmada!";
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Premium Header with Gradient */}
      <div className={cn(
        "relative overflow-hidden rounded-xl p-4 sm:p-5 mb-4",
        `bg-gradient-to-br ${theme.gradient}`
      )}>
        {/* Decorative blobs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-white/10 rounded-full blur-2xl animate-pulse" />
          <div className="absolute bottom-0 left-0 w-20 h-20 sm:w-24 sm:h-24 bg-white/10 rounded-full blur-xl animate-pulse" style={{ animationDelay: "1s" }} />
        </div>
        
        <div className="relative z-10 flex items-center gap-3 sm:gap-4">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <ThemeIcon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
              <span>{theme.emoji}</span>
              <span className="truncate">{getStepTitle()}</span>
            </h2>
            <p className="text-white/80 text-sm truncate">
              {currentStep === "confirmation" ? theme.confirmationText : theme.motivationalText}
            </p>
          </div>
        </div>
      </div>

      {/* Step Indicator - only show if not confirmation */}
      {currentStep !== "confirmation" && (
        <div className="relative mb-6 px-1">
          <StepIndicator steps={steps} currentStep={currentStep} theme={theme} />
        </div>
      )}

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto px-1 pb-4">
        {/* Step 1: Service Selection */}
        {currentStep === "service" && (
          <div className="space-y-3">
            {servicesLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-28 rounded-xl animate-shimmer" />
                ))}
              </div>
            ) : activeServices.length === 0 ? (
              <div className="text-center py-12">
                <ThemeIcon className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">
                  No hay {config.serviceLabels.plural.toLowerCase()} disponibles
                </p>
              </div>
            ) : (
              activeServices.map((service, i) => (
                <div 
                  key={service.id}
                  className="animate-slide-up-fade"
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  <ServiceCard
                    service={service}
                    isSelected={bookingData.service?.id === service.id}
                    onSelect={() => handleSelectService(service)}
                    theme={theme}
                  />
                </div>
              ))
            )}
          </div>
        )}

        {/* Step 2: Location Selection */}
        {currentStep === "location" && (
          <div className="space-y-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="mb-2 -ml-2 touch-manipulation"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Cambiar {config.serviceLabels.singular.toLowerCase()}
            </Button>

            <BookingSummary service={bookingData.service} date={null} theme={theme} compact />

            <LocationSelector
              locations={activeLocations.map(loc => ({
                id: loc.id,
                name: loc.name,
                address: loc.address,
                city: loc.city,
                is_primary: loc.is_primary,
              }))}
              selectedLocationId={bookingData.selectedLocationId}
              onSelect={handleSelectLocation}
              theme={theme}
            />
          </div>
        )}

        {/* Step 3: Date & Time Selection */}
        {currentStep === "datetime" && (
          <div className="space-y-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="mb-2 -ml-2 touch-manipulation"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              {hasMultipleLocations ? "Cambiar ubicación" : `Cambiar ${config.serviceLabels.singular.toLowerCase()}`}
            </Button>

            <BookingSummary service={bookingData.service} date={null} theme={theme} compact />

            <AvailabilityCalendar
              availability={activeAvailability.map(a => ({
                id: a.id,
                day_of_week: a.day_of_week,
                start_time: a.start_time,
                end_time: a.end_time,
                is_active: a.is_active,
              }))}
              blockedDates={blockedDates.map(b => ({
                blocked_date: b.blocked_date,
                reason: b.reason,
              }))}
              bookedSlots={bookedSlots}
              onSelectSlot={handleSelectSlot}
              theme={theme}
            />
          </div>
        )}

        {/* Step 4: Client Details */}
        {currentStep === "details" && (
          <div className="space-y-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="mb-2 -ml-2 touch-manipulation"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Cambiar fecha/hora
            </Button>

            <BookingSummary 
              service={bookingData.service} 
              date={bookingData.date}
              startTime={bookingData.startTime}
              endTime={bookingData.endTime}
              theme={theme}
              compact
            />

            {/* Client form with improved styling */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="clientName" className="text-base flex items-center gap-1.5 mb-2">
                  <User className="h-4 w-4 text-primary" />
                  Nombre completo *
                </Label>
                <Input
                  id="clientName"
                  value={bookingData.clientName}
                  onChange={(e) =>
                    setBookingData((prev) => ({ ...prev, clientName: e.target.value }))
                  }
                  placeholder="Tu nombre"
                  className="h-12 text-base"
                  required
                />
              </div>

              <div>
                <Label htmlFor="clientEmail" className="text-base flex items-center gap-1.5 mb-2">
                  <Mail className="h-4 w-4 text-primary" />
                  Email *
                </Label>
                <Input
                  id="clientEmail"
                  type="email"
                  value={bookingData.clientEmail}
                  onChange={(e) =>
                    setBookingData((prev) => ({ ...prev, clientEmail: e.target.value }))
                  }
                  placeholder="tu@email.com"
                  className="h-12 text-base"
                  required
                />
              </div>

              <div>
                <Label htmlFor="clientPhone" className="text-base flex items-center gap-1.5 mb-2">
                  <Phone className="h-4 w-4 text-primary" />
                  Teléfono
                </Label>
                <Input
                  id="clientPhone"
                  type="tel"
                  value={bookingData.clientPhone}
                  onChange={(e) =>
                    setBookingData((prev) => ({ ...prev, clientPhone: e.target.value }))
                  }
                  placeholder="+34 600 000 000"
                  className="h-12 text-base"
                />
              </div>

              <div>
                <Label htmlFor="notes" className="text-base flex items-center gap-1.5 mb-2">
                  <MessageSquare className="h-4 w-4 text-primary" />
                  Notas (opcional)
                </Label>
                <Textarea
                  id="notes"
                  value={bookingData.notes}
                  onChange={(e) =>
                    setBookingData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  placeholder="Información adicional..."
                  rows={3}
                  className="text-base resize-none"
                />
              </div>
            </div>

            <Button
              className={cn(
                "w-full h-12 text-base touch-manipulation",
                `bg-gradient-to-r ${theme.gradient} hover:opacity-90`
              )}
              onClick={() => setCurrentStep("payment")}
              disabled={!bookingData.clientName || !bookingData.clientEmail}
            >
              Continuar al pago
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Step 5: Payment Method */}
        {currentStep === "payment" && (
          <div className="space-y-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="mb-2 -ml-2 touch-manipulation"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Volver
            </Button>

            <BookingSummary 
              service={bookingData.service}
              date={bookingData.date}
              startTime={bookingData.startTime}
              clientName={bookingData.clientName}
              clientEmail={bookingData.clientEmail}
              theme={theme}
              compact
            />

            <PaymentMethodSelector
              value={bookingData.paymentMethod}
              onChange={(v) => setBookingData((prev) => ({ ...prev, paymentMethod: v }))}
              amount={bookingData.service?.price || 0}
              serviceName={bookingData.service?.name || "Servicio"}
              showPayPal={true}
              theme={theme}
            />

            <Button
              className={cn(
                "w-full h-12 text-base touch-manipulation shadow-lg",
                `bg-gradient-to-r ${theme.gradient} hover:opacity-90`
              )}
              size="lg"
              onClick={handleCreateBooking}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : bookingData.paymentMethod === "in_person" ? (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Confirmar Reserva
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Pagar {bookingData.service?.price}€
                </>
              )}
            </Button>
          </div>
        )}

        {/* Step 6: Confirmation - Premium Design */}
        {currentStep === "confirmation" && (
          <div className="text-center space-y-6 py-4">
            {/* Animated success icon */}
            <div className="relative mx-auto w-24 h-24">
              <div className={cn(
                "absolute inset-0 rounded-full animate-ping-slow",
                `bg-gradient-to-br ${theme.gradient} opacity-30`
              )} />
              <div className={cn(
                "relative w-24 h-24 rounded-full flex items-center justify-center animate-scale-in",
                `bg-gradient-to-br ${theme.gradient}`
              )}>
                <CheckCircle2 className="h-12 w-12 text-white" />
              </div>
            </div>

            <div className="space-y-2 animate-slide-up-fade" style={{ animationDelay: "200ms" }}>
              <h3 className="text-2xl sm:text-3xl font-bold flex items-center justify-center gap-2">
                <span>{theme.emoji}</span>
                <span>¡Reserva Confirmada!</span>
              </h3>
              <p className={cn(
                "text-lg font-medium",
                `bg-gradient-to-r ${theme.gradient} bg-clip-text text-transparent`
              )}>
                {theme.confirmationText}
              </p>
            </div>

            {bookingData.service && bookingData.date && (
              <Card className="text-left overflow-hidden animate-slide-up-fade" style={{ animationDelay: "400ms" }}>
                <div className={cn(
                  "h-2 w-full",
                  `bg-gradient-to-r ${theme.gradient}`
                )} />
                <CardContent className="p-4 sm:p-5 space-y-4">
                  <div className="flex items-center gap-3 pb-3 border-b">
                    <ThemeIcon className="h-6 w-6 text-primary" />
                    <div>
                      <p className="font-semibold">{bookingData.service.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {format(bookingData.date, "EEEE d 'de' MMMM", { locale: es })}
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Hora</p>
                      <p className="font-medium">{bookingData.startTime} - {bookingData.endTime}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Duración</p>
                      <p className="font-medium">{bookingData.service.duration_minutes} min</p>
                    </div>
                  </div>
                  
                  <div className="pt-3 border-t flex justify-between items-center">
                    <span className="text-muted-foreground">Total</span>
                    <span className={cn(
                      "text-2xl font-bold",
                      `bg-gradient-to-r ${theme.gradient} bg-clip-text text-transparent`
                    )}>
                      {bookingData.service.price}€
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            <p className="text-sm text-muted-foreground animate-slide-up-fade" style={{ animationDelay: "600ms" }}>
              📧 Hemos enviado los detalles a {bookingData.clientEmail}
            </p>

            <Button 
              onClick={resetBooking} 
              variant="outline" 
              className="w-full h-12 text-base touch-manipulation animate-slide-up-fade"
              style={{ animationDelay: "800ms" }}
            >
              Cerrar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export const UniversalBookingWidget = memo(function UniversalBookingWidget({
  providerType,
  providerId,
  providerName,
  providerSlug,
  className,
}: UniversalBookingWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();
  const config = useMemo(() => getBookingConfig(providerType), [providerType]);
  const theme = useMemo(() => getBookingTheme(providerType), [providerType]);

  const handleClose = useCallback(() => setIsOpen(false), []);

  const triggerButton = (
    <Button 
      className={cn(
        "gap-2 h-11 px-5 touch-manipulation shadow-lg hover:shadow-xl transition-all",
        `bg-gradient-to-r ${theme.gradient} hover:opacity-90`,
        className
      )}
    >
      <Sparkles className="h-4 w-4" />
      <span className="hidden sm:inline">Reservar</span> {config.serviceLabels.singular}
    </Button>
  );

  const content = (
    <UniversalBookingWidgetContent
      providerType={providerType}
      providerId={providerId}
      providerName={providerName}
      providerSlug={providerSlug}
      onClose={handleClose}
    />
  );

  // Use Drawer for mobile, Sheet for desktop
  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={setIsOpen}>
        <DrawerTrigger asChild>
          {triggerButton}
        </DrawerTrigger>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader className="sr-only">
            <DrawerTitle>Reservar {config.serviceLabels.singular}</DrawerTitle>
            <DrawerDescription>Reserva con {providerName}</DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-6 pt-2 overflow-y-auto max-h-[calc(90vh-3rem)]">
            {content}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        {triggerButton}
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="sr-only">
          <SheetTitle>Reservar {config.serviceLabels.singular}</SheetTitle>
          <SheetDescription>Reserva con {providerName}</SheetDescription>
        </SheetHeader>
        <div className="pt-2">
          {content}
        </div>
      </SheetContent>
    </Sheet>
  );
});
